import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Recycle,
  Calendar,
  Layers,
  CircleCheck,
} from "lucide-react";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const ANTONY_BESTSELLER_SLUGS = [
  "carton-standard",
  "carton-100-verres",
  "pack-cartons-demenagement-maison",
  "carton-tableaux-ecrans-plats",
  "papier-bulle-1x100m",
  "diable",
];

const Sections = ({ city }) => {
  const [bestsellers, setBestsellers] = useState([]);

  useEffect(() => {
    fetchProducts()
      .then((res) => {
        const all = res || [];
        const picked = ANTONY_BESTSELLER_SLUGS
          .map((s) => all.find((p) => p.slug === s))
          .filter(Boolean);
        setBestsellers(picked);
      })
      .catch(() => setBestsellers([]));
  }, []);

  return (
    <>
      {/* ======= SECTION 2 — Livraison à domicile + 3 engagements ======= */}
      <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons de déménagement à domicile à {city.name}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Ne perdez plus de temps à courir les magasins pour trouver des
              emballages. Nous simplifions vos préparatifs en livrant
              l&apos;ensemble de vos cartons et fournitures directement chez
              vous à {city.name} et partout en Île-de-France. Avec notre
              propre flotte de véhicules, nous vous garantissons un service
              rapide et fiable. Recevez votre matériel au pied de votre
              porte et commencez à emballer vos biens l&apos;esprit
              tranquille.
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              {
                title: "Propre flotte de transport",
                body: "Aucun sous-traitant, nous livrons vos cartons nous-mêmes avec nos propres véhicules pour un soin maximal.",
                Icon: Truck,
              },
              {
                title: "Rapidité et ponctualité",
                body: "Des créneaux respectés pour vous permettre de commencer vos préparatifs sans perdre une minute.",
                Icon: Calendar,
              },
              {
                title: "Soin de vos produits",
                body: "Vos cartons et protections arrivent en parfait état, prêts à l'emploi, directement entre vos mains.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white rounded-2xl border border-[#cfdbe6] p-5"
              >
                <div className="flex items-center gap-2.5">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b]">
                    {title}
                  </h3>
                </div>
                <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ======= SECTION 3 — Engagement vert : 100% recyclable ======= */}
      <section className="py-14 md:py-20 bg-[#F0FAF3] border-y border-[#BCE5C9]">
        <div className="container-cdm">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#16A34A]/10 border border-[#16A34A]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#15803D] mb-4">
                <Recycle className="h-3.5 w-3.5" strokeWidth={2.6} />
                Engagement éco-responsable
              </div>
              <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
                Un engagement vert : nos cartons sont 100&nbsp;% recyclables
              </h2>
              <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
                Parce que déménager peut aussi se faire dans le respect de
                l&apos;environnement, tous nos cartons sont fabriqués à
                partir de matériaux recyclables et biodégradables. Conçus
                pour être robustes et réutilisables, ils protègent vos
                affaires tout en limitant votre empreinte écologique. Faites
                le choix d&apos;un emballage de qualité professionnelle,
                durable et respectueux de la planète pour votre projet à
                {" "}{city.name}.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/cartons-reconditionnes"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#16A34A] hover:bg-[#15803D] text-white font-bold text-[14px] transition-colors"
                  data-testid="city-antony-cta-eco"
                >
                  <Recycle className="h-4 w-4" strokeWidth={2.6} />
                  Voir nos cartons reconditionnés
                  <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 grid grid-cols-1 gap-3">
              {[
                "Matériaux 100 % recyclables et biodégradables",
                "Conception robuste pour permettre la réutilisation",
                "Filière française et fournisseurs sélectionnés",
                "Tri facilité grâce à des cartons mono-matériau",
              ].map((txt) => (
                <div
                  key={txt}
                  className="flex items-start gap-3 bg-white rounded-2xl border border-[#BCE5C9] p-4"
                >
                  <span className="flex items-center justify-center h-8 w-8 rounded-full bg-[#16A34A] text-white shrink-0">
                    <CircleCheck className="h-4 w-4" strokeWidth={2.6} />
                  </span>
                  <span className="text-[14px] text-[#3a4856] leading-relaxed">
                    {txt}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ======= CTA Big Déménagement (au milieu) ======= */}
      <section className="py-12 bg-white">
        <div className="container-cdm">
          <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
        </div>
      </section>

      {/* ======= SECTION 4 — Meilleures ventes à Antony ======= */}
      <section className="py-14 md:py-20 bg-white">
        <div className="container-cdm">
          <div className="max-w-3xl">
            <div className="overline" style={{ color: "#FF7A00" }}>
              Top ventes locales
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Nos meilleures ventes à {city.name} : les indispensables de
              votre déménagement
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Découvrez les fournitures les plus plébiscitées par les
              habitants d&apos;{city.name} pour réussir leurs préparatifs. De
              notre carton standard ultra-polyvalent aux kits complets
              adaptés aux appartements et maisons de la région, ces produits
              phares combinent robustesse professionnelle et
              écoresponsabilité. Faites confiance aux choix de nos clients
              locaux pour sécuriser vos objets précieux au meilleur prix.
            </p>
          </div>

          {bestsellers.length === 0 ? (
            <div className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="aspect-square bg-[#EEF3F7] animate-pulse rounded-2xl border border-[#cfdbe6]"
                />
              ))}
            </div>
          ) : (
            <div
              className="mt-8 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
              data-testid="city-antony-bestsellers"
            >
              {bestsellers.map((p, idx) => (
                <ProductCard key={p.id} product={p} index={idx} hideQty goToProduct />
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/cartons-demenagement"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-antony-cta-shop"
            >
              Voir tout le catalogue
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
            <Link
              to="/packs-cartons"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
              data-testid="city-antony-cta-kits"
            >
              <Layers className="h-4 w-4" strokeWidth={2.6} />
              Choisir un kit
            </Link>
          </div>
        </div>
      </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-antony-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
    </>
  );
};

export default { Sections };
